import { GlobalState } from '@/global';
import { OtelState } from '@/otel';
import { Logger } from '@/shared/services/logger';
import { UserError, uuid } from '@signal24/vue-foundation';
import { compact } from 'lodash';

export interface IMobileConfig {
    deviceId: string;
    insets: { top: number; left: number; bottom: number; right: number };
}

export interface IMobileBridgeMessages {
    setTitle: {
        request: { title: string };
        response: boolean;
    };
    getHeadersForRequest: {
        request: {
            method: string;
            url: string;
            headers: Record<string, string>;
            isAuthRequest?: boolean;
        };
        response: {
            headers: Record<string, string>;
        };
    };
    readPayment: {
        request: {
            clientSecret: string;
        };
        response: { paymentIntentId: string };
    };
    cancelPayment: {
        request: undefined;
        response: boolean;
    };
}

const logger = Logger.createScoped('MobileBridge');

export class MobileBridge {
    static isWithinZynoSuiteMobile = false;
    static injectedConfig: IMobileConfig | null = null;
    // oxlint-disable-next-line no-explicit-any
    static resolvers: Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void }> = new Map();

    static init() {
        if (import.meta.env.VITE_APP_ENABLE_FAKE_MOBILE_BRIDGE === 'true') {
            GlobalState.enableMobileAppDesign.value = true;
        }

        try {
            if (!/ ZMA:app.zyno.suite(-(dev|alpha))?\//.test(navigator.userAgent)) throw new Error('user agent does not match');
            if (!('ReactNativeWebView' in window)) throw new Error('ReactNativeWebView not found in window');
            logger.info('Detected ZynoSuite Mobile WebView environment');
            this.isWithinZynoSuiteMobile = true;
            GlobalState.enableMobileAppDesign.value = true;
            OtelState.installId = this.config?.deviceId;

            window.addEventListener('message', event => {
                if (typeof event.data !== 'string') return;
                if (!event.data.startsWith('ZIPCResponse:')) return;
                return this.handleIncomingMessage(event.data.substring(13));
            });
        } catch {
            /**/
        }
    }

    static get config(): IMobileConfig | null {
        if (!('ReactNativeWebView' in window)) {
            return null;
        }
        if (this.injectedConfig === null) {
            // oxlint-disable-next-line no-explicit-any
            const json: any = JSON.parse((window as any).ReactNativeWebView.injectedObjectJson());
            this.injectedConfig = json.ZSConfig;
        }
        return this.injectedConfig;
    }

    static sendRequest<T extends keyof IMobileBridgeMessages>(
        type: T,
        data: IMobileBridgeMessages[T]['request']
    ): Promise<IMobileBridgeMessages[T]['response']> {
        return new Promise<IMobileBridgeMessages[T]['response']>((resolve, reject) => {
            const requestId = uuid();
            this.resolvers.set(requestId, { resolve, reject });
            // oxlint-disable-next-line no-explicit-any
            (window as any).ReactNativeWebView.postMessage(`ZIPCRequest:${JSON.stringify({ type, data, requestId })}`);
        });
    }

    private static handleIncomingMessage(messageJson: string) {
        const message = JSON.parse(messageJson);
        const { requestId, data, error } = message;
        const resolver = this.resolvers.get(requestId);
        if (resolver) {
            if (error) {
                const msg = compact([`${error.message}`, !!error.code && `[${error.code}]`]).join(' ');
                const err = 'isUserError' in error && error.isUserError ? new UserError(msg) : new Error(msg);
                resolver.reject(err);
            } else {
                resolver.resolve(data);
            }
            this.resolvers.delete(requestId);
        }
    }
}

MobileBridge.init();
